import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import {
  Loader2,
  Sparkles,
  BookOpen,
  Pencil,
} from 'lucide-react'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import MainHeader from '@/components/layout/MainHeader'
import { Main } from '@/components/main'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Button } from '@deepguide-ai/ui/button'
import { Badge } from '@deepguide-ai/ui/badge'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@deepguide-ai/ui/breadcrumb'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@deepguide-ai/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@deepguide-ai/ui/tabs'
import {
  DetailPanelTitle,
  DetailSection,
  DetailStatusGrid,
  DetailStatusItem,
} from '@/components/compliance/detail-primitives'
import { TrainingStatusBadge } from '@/components/compliance/people/TrainingStatusBadge'
import { TrainingProgramDialog } from '@/components/compliance/people/TrainingProgramDialog'
import { Response } from '@/components/ai-elements/response'
import PageLoading from '@/components/PageLoading'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/people_training/$programId'
)({
  component: TrainingProgramDetailPage,
  staticData: {
    pageHeader: { title: 'Training', customHeader: true, pad: false },
  },
})

function TrainingProgramDetailPage() {
  const { workspaceId, programId } = useParams({ strict: false }) as {
    workspaceId: string
    programId: string
  }
  const queryClient = useQueryClient()
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const [activeTab, setActiveTab] = useState('content')
  const [editOpen, setEditOpen] = useState(false)

  // Queries
  const programQuery = useQuery(
    trpc.training.getProgram.queryOptions({ id: programId })
  )
  const recordsQuery = useQuery(
    trpc.training.listRecords.queryOptions({ programId })
  )
  const quizQuery = useQuery(
    trpc.trainingQuiz.get.queryOptions({ programId })
  )
  // Mutations
  const generateQuizMutation = useMutation({
    ...trpc.trainingQuiz.generate.mutationOptions(),
    onSuccess: () => {
      toast.success('Training content & quiz generated')
      queryClient.invalidateQueries({ queryKey: trpc.trainingQuiz.get.queryKey({ programId }) })
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const publishQuizMutation = useMutation({
    ...trpc.trainingQuiz.publish.mutationOptions(),
    onSuccess: () => {
      toast.success('Quiz published. Employees can now take it')
      queryClient.invalidateQueries({ queryKey: trpc.trainingQuiz.get.queryKey({ programId }) })
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const program = programQuery.data
  const quiz = quizQuery.data
  const records = recordsQuery.data ?? []

  if (programQuery.isLoading) return <PageLoading />

  if (!program) {
    return (
      <div className='flex h-[50vh] items-center justify-center'>
        <p className='text-muted-foreground'>Training program not found</p>
      </div>
    )
  }

  const completedCount = records.filter((r: any) => r.status === 'COMPLETED').length
  const totalAssigned = records.length
  const quizQuestions = (quiz?.questions as any[]) ?? []
  const quizStatus = !quiz?.trainingContent
    ? 'Not generated'
    : quiz.published
      ? 'Published'
      : 'Draft'

  return (
    <div className='flex h-full min-h-0 w-full flex-col overflow-hidden'>
      <MainHeader className='!mb-0 !h-[50px] p-0 pl-4'>
        <div className='flex  items-center gap-2 px-1'>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/workspaces/$workspaceId/people' params={{ workspaceId }}>
                    People
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/workspaces/$workspaceId/people' params={{ workspaceId }} search={{ tab: 'training' }}>
                    Training
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className='truncate max-w-[400px]'>
                  {program.name}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </MainHeader>
      <ScrollArea className='min-h-0 flex-1' viewClassName='[&>div]:!block'>
      <Main>
        <div className='px-6 py-8 max-w-5xl mx-auto'>
          {/* Title */}
          <DetailPanelTitle
            className='relative pr-20'
            title={
              <>
                {program.name}
                <span
                  className={`font-body ml-2.5 inline-flex h-[20px] items-center rounded-md px-2 align-middle text-[10px] font-semibold tracking-[0.5px] whitespace-nowrap uppercase ${
                    program.required
                      ? 'bg-status-blue-bg text-status-blue'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {program.required ? 'Required' : 'Optional'}
                </span>
              </>
            }
          >
            {program.description && (
              <p className='font-body mt-2 text-[13px] leading-[1.6] text-muted-foreground'>
                {program.description}
              </p>
            )}
            {canWorkspaceWrite && (
              <Button
                size='sm'
                className='absolute top-0 right-0'
                onClick={() => setEditOpen(true)}
              >
                <Pencil className='size-3.5' />
                Edit
              </Button>
            )}
          </DetailPanelTitle>

          {/* Property section */}
          <DetailStatusGrid className='mt-5 !max-w-none grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3 lg:grid-cols-5'>
            <DetailStatusItem label='Due'>
              <span className='font-body text-muted-foreground text-[13px]'>
                {program.dueDateDays ? `${program.dueDateDays} days` : '—'}
              </span>
            </DetailStatusItem>
            <DetailStatusItem label='Renews'>
              <span className='font-body text-muted-foreground text-[13px]'>
                {program.renewalDays ? `Every ${program.renewalDays} days` : '—'}
              </span>
            </DetailStatusItem>
            <DetailStatusItem label='Assigned'>
              <span className='font-body text-muted-foreground text-[13px] tabular-nums'>
                {totalAssigned}
              </span>
            </DetailStatusItem>
            <DetailStatusItem label='Completed'>
              {totalAssigned === 0 ? (
                <span className='font-body text-text-placeholder text-[13px]'>—</span>
              ) : (
                <div className='flex items-center gap-2'>
                  <span className='font-body text-muted-foreground text-[13px] tabular-nums'>
                    {completedCount} / {totalAssigned}
                  </span>
                  <div className='h-1.5 w-16 overflow-hidden rounded-full bg-muted'>
                    <div
                      className={`h-full rounded-full transition-[width] duration-500 ease-out ${
                        completedCount >= totalAssigned ? 'bg-status-green' : 'bg-status-orange'
                      }`}
                      style={{ width: `${Math.round((completedCount / totalAssigned) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </DetailStatusItem>
            <DetailStatusItem label='Quiz'>
              <span
                className={`font-body inline-flex h-5 items-center rounded-sm px-2 text-[11px] font-medium ${
                  quizStatus === 'Published'
                    ? 'bg-status-green-bg text-status-green'
                    : quizStatus === 'Draft'
                    ? 'bg-status-orange-bg text-status-orange'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {quizStatus}
              </span>
            </DetailStatusItem>
          </DetailStatusGrid>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className='mt-6'>
            <TabsList className='w-full'>
              <TabsTrigger value='content'>Content</TabsTrigger>
              <TabsTrigger value='quiz'>
                Quiz{quizQuestions.length > 0 ? ` (${quizQuestions.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value='employees'>
                Employees{totalAssigned > 0 ? ` (${totalAssigned})` : ''}
              </TabsTrigger>
            </TabsList>

            {/* Content tab */}
            <TabsContent value='content' className='mt-4'>
              <DetailSection title='Training Content'>
                {quiz?.trainingContent ? (
                  <div className='max-w-none'>
                    <Response>{quiz.trainingContent}</Response>
                  </div>
                ) : (
                  <div className='rounded-sm border border-border bg-card text-center py-10 space-y-3'>
                    <BookOpen className='h-10 w-10 mx-auto text-text-placeholder' />
                    <p className='text-sm text-text-subtle'>No training content generated yet</p>
                    {canWorkspaceWrite && (
                      <Button
                        onClick={() => {
                          // @ts-expect-error - Known tRPC mutation type inference issue
                          generateQuizMutation.mutate({ programId })
                        }}
                        disabled={generateQuizMutation.isPending}
                        className='gap-1.5'
                      >
                        {generateQuizMutation.isPending ? (
                          <Loader2 className='h-3.5 w-3.5 animate-spin' />
                        ) : (
                          <Sparkles className='h-3.5 w-3.5' />
                        )}
                        Generate Training Content & Quiz
                      </Button>
                    )}
                  </div>
                )}
              </DetailSection>
            </TabsContent>

            {/* Quiz tab */}
            <TabsContent value='quiz' className='mt-4'>
              {quizQuestions.length > 0 ? (
                <DetailSection
                  title={`Quiz · ${quizQuestions.length} questions · Pass ${quiz?.passThreshold}%`}
                  actionPosition='right'
                  headerActions={
                    quiz?.published ? (
                      <Badge
                        variant='outline'
                        className='text-xs text-status-green-strong border-status-green/30 bg-status-green-bg'
                      >
                        Published
                      </Badge>
                    ) : canWorkspaceWrite ? (
                      <Button
                        size='sm'
                        onClick={() => {
                          // @ts-expect-error - Known tRPC mutation type inference issue
                          publishQuizMutation.mutate({ quizId: quiz.id })
                        }}
                        disabled={publishQuizMutation.isPending}
                        className='gap-1.5'
                      >
                        Publish Quiz
                      </Button>
                    ) : (
                      <Badge
                        variant='outline'
                        className='text-xs text-status-orange-strong border-status-orange/30 bg-status-orange-bg'
                      >
                        Draft
                      </Badge>
                    )
                  }
                >
                  <div className='space-y-4'>
                    {quizQuestions.map((q: any, i: number) => (
                      <div key={i} className='rounded-sm border border-border bg-card p-4 space-y-2'>
                        <p className='text-sm font-medium'>
                          <span className='text-text-subtle mr-2'>Q{i + 1}.</span>
                          {q.question}
                        </p>
                        <div className='grid grid-cols-2 gap-1.5'>
                          {q.options.map((opt: string, j: number) => (
                            <div
                              key={j}
                              className={`rounded-sm px-2.5 py-1.5 text-xs ${
                                j === q.correctIndex
                                  ? 'bg-status-green-bg border border-status-green/30 text-status-green-strong'
                                  : 'bg-surface-subtle border border-border text-text-body'
                              }`}
                            >
                              {opt}
                            </div>
                          ))}
                        </div>
                        <p className='text-xs text-text-subtle italic'>{q.explanation}</p>
                      </div>
                    ))}
                  </div>
                </DetailSection>
              ) : (
                <div className='rounded-sm border border-border bg-card text-center py-10 space-y-2'>
                  <Sparkles className='h-10 w-10 mx-auto text-text-placeholder' />
                  <p className='text-sm text-text-subtle'>
                    No quiz yet. Generate training content to create one.
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Employees tab */}
            <TabsContent value='employees' className='mt-4'>
              <DetailSection title={`Employees · ${totalAssigned} assigned`}>
                {records.length > 0 ? (
                  <div className='w-full overflow-hidden rounded-sm border border-border bg-card transition-all'>
                    <Table className='font-body'>
                      <TableHeader>
                        <TableRow className='h-[40px] bg-surface-subtle hover:bg-surface-subtle'>
                          <TableHead className='font-body px-3 text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                            Employee
                          </TableHead>
                          <TableHead className='font-body w-[130px] px-3 text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                            Status
                          </TableHead>
                          <TableHead className='font-body w-[120px] px-3 text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                            Due Date
                          </TableHead>
                          <TableHead className='font-body w-[120px] px-3 text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                            Completed
                          </TableHead>
                          <TableHead className='font-body w-[80px] px-3 text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                            Score
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {records.map((rec: any) => (
                          <TableRow
                            key={rec.id}
                            className='hover:bg-surface-subtle'
                          >
                            <TableCell className='px-3 py-2.5'>
                              <span className='line-clamp-1 text-[12px] font-medium text-foreground'>
                                {rec.employee
                                  ? `${rec.employee.firstName} ${rec.employee.lastName}`
                                  : rec.employeeId}
                              </span>
                            </TableCell>
                            <TableCell className='px-3 py-2.5'>
                              <TrainingStatusBadge status={rec.status} />
                            </TableCell>
                            <TableCell className='px-3 py-2.5 text-[11px] text-muted-foreground'>
                              {rec.dueDate
                                ? format(
                                    new Date(rec.dueDate),
                                    'MMM d, yyyy'
                                  )
                                : '—'}
                            </TableCell>
                            <TableCell className='px-3 py-2.5 text-[11px] text-muted-foreground'>
                              {rec.completedAt
                                ? format(
                                    new Date(rec.completedAt),
                                    'MMM d, yyyy'
                                  )
                                : '—'}
                            </TableCell>
                            <TableCell className='px-3 py-2.5 text-[11px] text-muted-foreground'>
                              {rec.score != null ? `${rec.score}%` : '—'}
                            </TableCell>
                          </TableRow>
                        ))}

                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <TableEmptyState message='No employees are assigned to this training.' />
                )}
              </DetailSection>
            </TabsContent>
          </Tabs>
          <TrainingProgramDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            program={program}
          />
        </div>
      </Main>
      </ScrollArea>
    </div>
  )
}
